import React, { FC } from "react";
import { useIntl, MessageDescriptor } from "react-intl";
import CloseIcon from "@mui/icons-material/Close";
import Radio from "../../Unknown/Radio";
import FormControlLabel from "../../Unknown/FormControlLabel";
import Typography from "../../Unknown/Typography";
import Box from "../../Unknown/Box";
import IconButton from "../../Unknown/IconButton";
import CircularProgress from "../../Unknown/CircularProgress";
import Button from "../../Unknown/Button";
import { CustomerInMerge, LinkedPatient, MergeFieldKey } from "./types";
import messages from "./messages";
import formatSelfPatientAddress from "./formatSelfPatientAddress";
import { useRaceTranslations } from "../../Patient/PatientEditForm/useRaceOptions";
import { useEthnicityTranslations } from "../../Patient/PatientEditForm/useEthnicityOptions";
import { useSexTranslations } from "../../Patient/PatientEditForm/useSexOptions";

type FieldDef = { key: MergeFieldKey; label: MessageDescriptor };

type FieldRow = FieldDef & { value: string | null };

type CustomerMergeColumnProps = {
  customer: CustomerInMerge;
  fieldSelections: Record<MergeFieldKey, string | null>;
  onFieldSelect: (field: MergeFieldKey, customerId: string) => void;
  showPatientFields: boolean;
  showRemove: boolean;
  onRemove: (customerId: string) => void;
  // AC12 — footer "Wards"/"Family members" name links → Patient drawer.
  onPatientClick: (mrn: string) => void;
};

const CUSTOMER_FIELD_ROWS: FieldDef[] = [
  { key: "firstName", label: messages.fieldFirstName },
  { key: "lastName", label: messages.fieldLastName },
  { key: "email", label: messages.fieldEmail },
  { key: "phone", label: messages.fieldPhone },
  { key: "additionalPhone", label: messages.fieldAdditionalPhone },
];

const PATIENT_FIELD_ROWS: FieldDef[] = [
  { key: "patientMrn", label: messages.fieldMrn },
  { key: "patientDateBirth", label: messages.fieldDateBirth },
  { key: "patientAddress", label: messages.fieldAddress },
  { key: "patientRace", label: messages.fieldRace },
  { key: "patientEthnicity", label: messages.fieldEthnicity },
  { key: "patientSex", label: messages.fieldSex },
];

const getCustomerFieldValue = (
  customer: CustomerInMerge,
  key: MergeFieldKey,
): string | null => {
  switch (key) {
    case "firstName":
      return customer.firstName || null;
    case "lastName":
      return customer.lastName || null;
    case "email":
      return customer.email || null;
    case "phone":
      return customer.phone;
    case "additionalPhone":
      return customer.additionalPhone;
    case "patientMrn":
      return customer.selfPatient?.mrn ?? null;
    case "patientDateBirth":
      return customer.selfPatient?.date_birth ?? null;
    case "patientAddress":
      return formatSelfPatientAddress(customer.selfPatient);
    case "patientRace":
      return customer.selfPatient?.race ?? null;
    case "patientEthnicity":
      return customer.selfPatient?.ethnicity ?? null;
    case "patientSex":
      return customer.selfPatient?.sex ?? null;
    default:
      return null;
  }
};

const PatientLinksSection: FC<{
  title: string;
  patients: LinkedPatient[];
  onPatientClick: (mrn: string) => void;
}> = ({ title, patients, onPatientClick }) => (
  <Box>
    <Typography variant="body2" fontWeight={400}>
      {title}
    </Typography>
    <Box display="flex" flexDirection="column" alignItems="flex-start">
      {patients.map((patient) => (
        <Button
          key={patient.id}
          variant="text"
          size="small"
          onClick={() => onPatientClick(patient.mrn)}
          sx={{
            p: 0,
            minWidth: 0,
            fontWeight: 400,
            textDecoration: "none",
            textTransform: "none",
            "&:hover": { textDecoration: "none" },
          }}
        >
          {patient.lastName}, {patient.firstName}
        </Button>
      ))}
    </Box>
  </Box>
);

const CustomerMergeColumn: FC<CustomerMergeColumnProps> = ({
  customer,
  fieldSelections,
  onFieldSelect,
  showPatientFields,
  showRemove,
  onRemove,
  onPatientClick,
}) => {
  const { formatMessage } = useIntl();

  // Race/ethnicity/sex are stored as DB enum values (e.g. "hispanic",
  // "prefer_not_to_answer") — humanize them for display using the same
  // translation maps PatientEditForm's dropdowns use, so the merge column
  // never shows a raw snake_case value to staff.
  const raceTranslations = useRaceTranslations();
  const ethnicityTranslations = useEthnicityTranslations();
  const sexTranslations = useSexTranslations();

  const getDisplayValue = (
    key: MergeFieldKey,
    value: string | null,
  ): string | null => {
    if (value === null) return null;
    switch (key) {
      case "patientRace":
        return (
          raceTranslations[value as keyof typeof raceTranslations] ?? value
        );
      case "patientEthnicity":
        return (
          ethnicityTranslations[value as keyof typeof ethnicityTranslations] ??
          value
        );
      case "patientSex":
        return sexTranslations[value as keyof typeof sexTranslations] ?? value;
      default:
        return value;
    }
  };

  const allRows: FieldRow[] = [
    ...CUSTOMER_FIELD_ROWS.map((f) => ({
      ...f,
      value: getCustomerFieldValue(customer, f.key),
    })),
    ...(showPatientFields
      ? PATIENT_FIELD_ROWS.map((f) => ({
          ...f,
          value: getCustomerFieldValue(customer, f.key),
        }))
      : []),
  ];

  const hasNoSelfPatient =
    customer.selfPatient === null && !customer.isLoadingSelfPatient;

  return (
    <Box
      sx={{
        // flexGrow fills extra space evenly when few columns fit; flexShrink: 0
        // + minWidth stop columns from being squeezed below a readable width —
        // once more columns don't fit, the row should scroll, not compress.
        flex: "1 1 240px",
        flexShrink: 0,
        minWidth: 240,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Column header */}
      <Box
        sx={{
          p: "16px",
          borderBottom: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.23)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Typography variant="h5" fontWeight={400} noWrap>
            {customer.lastName}, {customer.firstName}
          </Typography>
          {showRemove && (
            <IconButton
              size="small"
              aria-label={formatMessage(messages.removeCustomer)}
              sx={{ color: "error.main", mt: -0.5, mr: -0.5 }}
              onClick={() => onRemove(customer.id)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {formatMessage(messages.wards, { count: customer.wards.length })}
          </Typography>
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              bgcolor: "text.secondary",
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {formatMessage(messages.familyMembers, {
              count: customer.familyMembers.length,
            })}
          </Typography>
        </Box>
      </Box>

      {customer.isLoadingSelfPatient && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={16} />
        </Box>
      )}

      {/* Field rows */}
      {allRows.map((row, index) => {
        const isPatientField = PATIENT_FIELD_ROWS.some(
          (f) => f.key === row.key,
        );
        const isDisabled =
          row.value === null || (isPatientField && hasNoSelfPatient);
        const isSelected = fieldSelections[row.key] === customer.id;

        return (
          <Box
            key={row.key}
            sx={{
              pl: "8px",
              pr: "16px",
              py: "16px",
              borderTop: index === 0 ? "none" : "1px dashed",
              borderColor: "rgba(0, 0, 0, 0.23)",
              backgroundColor: isSelected ? "#1976D214" : "transparent",
            }}
          >
            <FormControlLabel
              sx={{ alignItems: "center", width: "100%", m: 0 }}
              control={
                <Radio
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={() => {
                    if (!isDisabled) onFieldSelect(row.key, customer.id);
                  }}
                />
              }
              label={
                <Box sx={{ ml: 0.5 }}>
                  <Typography
                    variant="caption"
                    color={isDisabled ? "text.disabled" : "text.secondary"}
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      display: "block",
                    }}
                  >
                    {formatMessage(row.label)}
                  </Typography>
                  {/* Always render this line (even when empty) so every row's height
                      stays consistent across columns — otherwise a customer with no
                      value for a field renders one line shorter than one that has a
                      value, and every row below it drifts out of alignment. */}
                  <Typography variant="body2" noWrap>
                    {getDisplayValue(row.key, row.value) ?? " "}
                  </Typography>
                </Box>
              }
            />
          </Box>
        );
      })}

      {/* AC12 — Wards/Family members footer, links to the Patient drawer.
          No radio — these aren't selectable fields, just informational
          cross-links. Hidden entirely when both lists are empty. */}
      {(customer.wards.length > 0 || customer.familyMembers.length > 0) && (
        <Box
          sx={{
            p: "16px",
            // Grow to fill any remaining column height (the parent row uses
            // alignItems: "stretch", so every column matches the tallest
            // sibling) instead of pinning to the bottom — that would leave a
            // gap ABOVE a shorter footer's content while a taller sibling's
            // footer fills the space, making them start at different
            // heights. Growing keeps every footer's content top-aligned
            // right after the field rows; only the empty grey space below
            // a shorter footer's content differs.
            flex: 1,
            borderTop: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.23)",
            backgroundColor: "action.hover",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {customer.wards.length > 0 && (
            <PatientLinksSection
              title={formatMessage(messages.footerWards)}
              patients={customer.wards}
              onPatientClick={onPatientClick}
            />
          )}
          {customer.familyMembers.length > 0 && (
            <PatientLinksSection
              title={formatMessage(messages.footerFamilyMembers)}
              patients={customer.familyMembers}
              onPatientClick={onPatientClick}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CustomerMergeColumn;
